import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  Button,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountTree as GraphIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../layout/DashboardLayout';
import { useDatabase } from '../../hooks/useDatabase';
import { Organization } from '../../types/database';
import { OrganizationModal } from './OrganizationModal';

export const OrganizationList: React.FC = () => {
  const navigate = useNavigate();
  const db = useDatabase();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data, error } = await db.getOrganizations();
        if (error) throw error;
        
        setOrganizations(data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, [db]);

  const handleSubmit = async (organization: Omit<Organization, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (selectedOrganization) {
        // Edit existing organization
        const { error } = await db.updateOrganization(selectedOrganization.id, organization);
        if (error) throw error;
      } else {
        // Create new organization
        const { error } = await db.createOrganization(organization);
        if (error) throw error;
      }
      
      setOpenDialog(false);
      setSelectedOrganization(null);
      // Refresh the organizations list
      const { data: organizationsData } = await db.getOrganizations();
      setOrganizations(organizationsData || []);
    } catch (err) {
      console.error('Error saving organization:', err);
      setError(err instanceof Error ? err.message : 'Failed to save organization');
    }
  };

  const handleAddOrganization = () => {
    setSelectedOrganization(null);
    setOpenDialog(true);
  };

  const handleEditOrganization = (event: React.MouseEvent, organization: Organization) => {
    event.stopPropagation();
    setSelectedOrganization(organization);
    setOpenDialog(true);
  };

  const handleDelete = async (event: React.MouseEvent, id: string) => {
    event.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this organization?')) {
      return;
    }

    try {
      const { error } = await db.deleteOrganization(id);
      if (error) throw error;
      
      setOrganizations(prev => prev.filter(org => org.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete organization');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Organizations">
      <Box sx={{ flexGrow: 1, p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2}>
          {/* Header Section */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddOrganization}
                sx={{ borderRadius: 1, height: 32 }}
              >
                Add Organization
              </Button>
            </Box>
          </Grid>

          {organizations.length === 0 ? (
            <Grid item xs={12}>
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No organizations yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create your first organization to get started
                </Typography>
              </Paper>
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Paper>
                <List>
                  {organizations.map((organization, index) => (
                    <React.Fragment key={organization.id}>
                      <ListItem
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/graph?focus=${organization.id}`);
                              }}
                              title="View in Galaxy"
                            >
                              <GraphIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={(e) => handleEditOrganization(e, organization)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              edge="end"
                              onClick={(e) => handleDelete(e, organization.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemButton
                          onClick={() => navigate(`/organizations/${organization.id}`)}
                        >
                          <ListItemText
                            primary={organization.name}
                            secondary={
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                {organization.description && (
                                  <Typography variant="body2" color="text.secondary">
                                    {organization.description}
                                  </Typography>
                                )}
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {organization.industry && (
                                    <Chip label={organization.industry} size="small" variant="outlined" />
                                  )}
                                  {organization.size && (
                                    <Chip label={organization.size} size="small" variant="outlined" />
                                  )}
                                  {organization.location && (
                                    <Chip label={organization.location} size="small" variant="outlined" />
                                  )}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < organizations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </Grid>
          )}
        </Grid>

        <OrganizationModal
          open={openDialog}
          onClose={() => {
            setOpenDialog(false);
            setSelectedOrganization(null);
          }}
          onSubmit={handleSubmit}
          organization={selectedOrganization}
        />
      </Box>
    </DashboardLayout>
  );
};
